import React from 'react';
import { CharacterPortrait, DialogueItem } from '../../types';
import { listConfiguredEmotions } from './emotion-presets';
import studio from './scenario-studio.module.css';
import inline from './inline-editor.module.css';

interface InlineDialogueMetaProps {
  item: DialogueItem;
  characters: CharacterPortrait[];
  editMode: boolean;
  onChange: (patch: Partial<DialogueItem>) => void;
}

export const InlineDialogueMeta: React.FC<InlineDialogueMetaProps> = ({
  item,
  characters,
  editMode,
  onChange,
}) => {
  const selectedChar = item.characterId
    ? characters.find((c) => c.id === item.characterId)
    : undefined;
  const moodOptions = selectedChar
    ? (() => {
        const keys = listConfiguredEmotions(selectedChar.emotions);
        if (keys.length > 0) return keys;
        return selectedChar.defaultImage ? (['neutre'] as const) : [];
      })()
    : [];
  const currentEmotion =
    item.emotion ??
    ((moodOptions as string[]).includes('neutre') ? 'neutre' : moodOptions[0] ?? '');
  const emotionOptions =
    currentEmotion && !(moodOptions as string[]).includes(currentEmotion)
      ? [...moodOptions, currentEmotion]
      : moodOptions;

  if (!editMode) {
    const name = selectedChar?.name ?? item.speaker ?? 'Personnage';
    return (
      <div className={inline.slMetaRow}>
        <span className={inline.slLabel}>
          {name}
          {selectedChar && item.placement ? ` · ${item.placement}` : ''}
          {currentEmotion ? ` · ${currentEmotion}` : ''}
        </span>
      </div>
    );
  }

  return (
    <div className={inline.slMetaRow}>
      <div className={inline.metaField}>
        <span className={inline.metaFieldLabel}>Personnage</span>
        <select
          className={`${studio.select} ${inline.metaSelect}`}
          value={item.characterId ?? ''}
          onChange={(e) => {
            const charId = e.target.value;
            if (!charId) {
              onChange({
                characterId: undefined,
                characterMood: undefined,
                emotion: undefined,
                isInternal: true,
                speaker: 'Héros (intérieur)',
                placement: undefined,
              });
              return;
            }
            const char = characters.find((c) => c.id === charId);
            if (!char) return;
            const emotions = listConfiguredEmotions(char.emotions);
            onChange({
              characterId: charId,
              speaker: char.name,
              emotion: emotions[0] ?? 'neutre',
              characterMood: undefined,
              isInternal: false,
              placement: item.placement ?? 'centre',
            });
          }}
        >
          <option value="">— Aucun —</option>
          {characters.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name} ({c.id})
            </option>
          ))}
        </select>
      </div>

      {(selectedChar || !item.isInternal) && (
        <div className={inline.metaField}>
          <span className={inline.metaFieldLabel}>Placement</span>
          <select
            className={`${studio.select} ${inline.metaSelect}`}
            value={item.placement ?? 'centre'}
            onChange={(e) =>
              onChange({ placement: e.target.value as DialogueItem['placement'] })
            }
          >
            <option value="gauche">Gauche</option>
            <option value="centre">Centre</option>
            <option value="droite">Droite</option>
          </select>
        </div>
      )}

      {selectedChar && moodOptions.length > 0 && (
        <div className={inline.metaField}>
          <span className={inline.metaFieldLabel}>Émotion</span>
          <select
            className={`${studio.select} ${inline.metaSelect}`}
            value={currentEmotion}
            onChange={(e) =>
              onChange({ emotion: e.target.value, characterMood: undefined })
            }
          >
            {emotionOptions.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        </div>
      )}

      {selectedChar && moodOptions.length === 0 && (
        <span className={studio.hint}>Aucune émotion configurée pour ce personnage.</span>
      )}
    </div>
  );
};
